// Phase 9.2.B — Portal order detail (2026-05-11).
//
// Spec source: docs/apps/client-portal.md § "Order Detail".
//
// Read-only view of one order: header, line items (with variants +
// decorations), client-visible proofs, shipments. No mutations — re-order
// CTA ships in Phase 9.5.
//
// Tenancy isolation enforced server-side (clientId tenancy filter on
// service queries; route returns 404 on cross-tenant access). Frontend
// just handles the 404 + the loading/error states.
//
// Phase 9.3.B (2026-05-12): proof gallery rewired to use the unified
// /portal/files/:id/download endpoint. Previously rendered via
// `r2PublicUrl(storageKey)` which returned null unless the Cloudflare
// bucket had public access. The presigned-download path works in any
// R2-configured env without bucket-level changes.
//
// CEO 2026-10-19: a progress timeline under the header (server-built from
// the order's own records — dates only where a record holds one), the
// order's invoices with links to /invoices/:id, and a Download link on every
// proof tile (same tenancy-checked /portal/files/:id/download endpoint).
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  type PortalLine,
  type PortalLineDecoration,
  type PortalLineVariant,
  type PortalOrderInvoice,
  type PortalOrderTimeline,
  type PortalProof,
  type ReorderUnpayableReason,
  INVOICE_TYPE_LABEL,
  PROOF_STATUS_LABEL,
  SHIPMENT_STATUS_LABEL,
  STAGE_LABEL,
  orderStatusWords,
  STAGE_PILL_CLASS,
  chargeLabel,
  fetchPortalOrder,
  formatDate,
  formatUsd,
  isReorderableStage,
  lineImage,
  lineName,
  reorderPortalOrder,
  unitPriceLabel,
} from "@/lib/portal-orders";
import { termsLabel } from "@/lib/portal-store";
import { STATUS_LABEL as INVOICE_STATUS_LABEL, STATUS_PILL_CLASS as INVOICE_STATUS_PILL_CLASS } from "@/lib/portal-invoices";
import { fetchPortalFileDownloadUrl } from "@/lib/portal-brand-assets";
import {
  approvePortalProof,
  fetchProofTerms,
  rejectPortalProof,
} from "@/lib/portal-attention";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

const REORDER_ERROR_LABEL: Record<ReorderUnpayableReason, string> = {
  stage_not_reorderable:
    "This order isn't ready to be re-ordered yet. Once it ships, you'll be able to clone it.",
  cancelled: "This order was cancelled, so it can't be re-ordered.",
};

/** Our SKU (ATS-10001) — the API puts it on the client-safe snapshot. */
function lineSku(l: PortalLine): string | null {
  const s = (l.productSnapshot as { sku?: unknown } | null)?.sku;
  return typeof s === "string" && s.trim() ? s.trim() : null;
}

function variantLabel(v: PortalLineVariant): string {
  const parts = [v.color, v.size, v.material].filter(
    (p): p is string => typeof p === "string" && p.length > 0,
  );
  return parts.length > 0 ? parts.join(" / ") : "Variant";
}

/** Location · method · size · colour — the details the client document shows
 *  per imprint location (colour already folds in ink colours / count). */
function decorationLabel(d: PortalLineDecoration): string {
  return [d.location?.trim() || "Decoration", d.method, d.size, d.color]
    .filter((p): p is string => typeof p === "string" && p.length > 0)
    .join(" • ");
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [reorderOpen, setReorderOpen] = useState(false);
  // Per-line review state, keyed by source line id. Initialized from the
  // order's lines when the review opens.
  const [reorderLines, setReorderLines] = useState<
    Record<string, { included: boolean; quantity: string }>
  >({});
  const [reorderNote, setReorderNote] = useState("");
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [reorderSuccess, setReorderSuccess] = useState<{
    orderNumber: number;
  } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["portal-order", id],
    queryFn: () => fetchPortalOrder(id!),
    enabled: typeof id === "string" && id.length > 0,
  });

  // Portal v2 Slice A — the dashboard's "Review proof" cards deep-link to
  // /orders/:id#proofs. React Router's client-side navigation never fires
  // the browser's native hash scroll (that only happens on a full page
  // load), so land the client on the section ourselves — and only once the
  // data has rendered, because the #proofs section doesn't exist before
  // that. Instant when the client prefers reduced motion.
  const location = useLocation();
  useEffect(() => {
    if (!data || location.hash !== "#proofs") return;
    const target = document.getElementById("proofs");
    if (!target) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [data, location.hash]);

  // Phase 9.5 — Re-order mutation.
  //
  // Server returns the new draft order id, but the client doesn't see
  // the draft (it lives in the rep's queue until they review pricing
  // and send back for approval). We just show a success toast with the
  // new order number for confidence.
  const reorderMutation = useMutation({
    mutationFn: () => {
      const lines = Object.entries(reorderLines)
        .filter(([, v]) => v.included)
        .map(([lineId, v]) => ({
          line_item_id: lineId,
          quantity: Number.parseInt(v.quantity, 10),
        }));
      return reorderPortalOrder(id!, {
        lines,
        note: reorderNote.trim() || undefined,
      });
    },
    onSuccess: (res) => {
      setReorderError(null);
      setReorderSuccess({ orderNumber: res.new_order_number });
      setReorderOpen(false);
      // Invalidate the orders list so the new draft appears (if the
      // backend ever surfaces drafts to portal — it doesn't today
      // because of the draft-suppression filter, but invalidating is
      // cheap insurance).
      void queryClient.invalidateQueries({ queryKey: ["portal-orders"] });
    },
    onError: (err) => {
      setReorderSuccess(null);
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { reason?: ReorderUnpayableReason } | null;
        setReorderError(
          body?.reason
            ? REORDER_ERROR_LABEL[body.reason]
            : "This order can't be re-ordered right now.",
        );
      } else if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { error?: string } | null;
        setReorderError(
          body?.error === "invalid_lines"
            ? "This order changed since the page loaded — refresh and try again."
            : "Check the quantities — each item needs a whole number between 1 and 100,000.",
        );
      } else if (err instanceof ApiError && err.status === 404) {
        setReorderError("This order is no longer available.");
      } else {
        setReorderError("Something went wrong. Try again in a moment.");
      }
    },
  });

  if (!user) return null;

  const notFound = isError && error instanceof ApiError && error.status === 404;
  const canReorder =
    data !== undefined && isReorderableStage(data.statusStage);

  return (
    <PortalLayout>
        <Link
          to="/orders"
          className="mb-6 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
        >
          ← All orders
        </Link>

        {isLoading && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center text-brown-300 shadow-sm">
            Loading order…
          </div>
        )}

        {notFound && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center shadow-sm">
            <p className="mb-2 font-display text-2xl font-bold tracking-tight text-brown-500">
              Order not found
            </p>
            <p className="mb-6 text-brown-400">
              This order doesn't exist, or it's not part of your account.
            </p>
            <Link
              to="/orders"
              className="inline-block rounded-md bg-ink px-6 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700"
            >
              Back to orders
            </Link>
          </div>
        )}

        {isError && !notFound && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-12 text-center shadow-sm">
            <p className="mb-2 text-rust-500">
              We couldn't load this order.
            </p>
            <p className="text-sm text-brown-400">
              Try refreshing the page in a moment.
            </p>
          </div>
        )}

        {data && (
          <>
            {/* Header card */}
            <section className="mb-8 rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <h1 className="font-display text-3xl font-bold tracking-tight">
                      Order #{data.orderNumber}
                    </h1>
                    <span
                      className={
                        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold " +
                        STAGE_PILL_CLASS[data.statusStage]
                      }
                    >
                      {orderStatusWords(data.statusStage, data.statusState)}
                    </span>
                  </div>
                  <p className="text-brown-400">{data.projectName}</p>
                </div>
                {data.totalAmount !== null && (
                  <div className="text-right">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brown-300">
                      Total
                    </div>
                    <div className="font-display text-3xl font-bold tracking-tight text-brown-500 tabular-nums">
                      {formatUsd(data.totalAmount)}
                    </div>
                  </div>
                )}
              </div>

              <dl className="grid grid-cols-2 gap-y-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Ordered
                  </dt>
                  <dd className="text-brown-500">
                    {formatDate(data.orderDate)}
                  </dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    In hands
                  </dt>
                  <dd className="text-brown-500">
                    {formatDate(data.inHandsDate)}
                  </dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Customer PO
                  </dt>
                  <dd className="text-brown-500">{data.customerPo ?? "—"}</dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Terms
                  </dt>
                  <dd className="text-brown-500">{termsLabel(data.terms)}</dd>
                </div>
              </dl>

              {data.introduction && (
                <p className="mt-6 border-t border-cream-200 pt-6 text-sm text-brown-400">
                  {data.introduction}
                </p>
              )}
            </section>

            {/* Progress — Ordered → … → Paid, from the order's own records. */}
            <OrderTimeline timeline={data.timeline} />

            {/* Re-order CTA — Phase 9.5 */}
            {canReorder && (
              <section className="mb-8 rounded-lg border border-peach-300 bg-peach-50 p-6 shadow-sm">
                {reorderSuccess ? (
                  <>
                    <h2 className="mb-2 font-display text-lg font-bold tracking-tight">
                      Re-order submitted
                    </h2>
                    <p className="text-brown-500">
                      We've forwarded your request to your account manager.
                      They'll review pricing and reach out to confirm. Your
                      new order number is{" "}
                      <span className="font-semibold tabular-nums">
                        #{reorderSuccess.orderNumber}
                      </span>
                      .
                    </p>
                  </>
                ) : !reorderOpen ? (
                  <>
                    <h2 className="mb-2 font-display text-lg font-bold tracking-tight">
                      Need another batch?
                    </h2>
                    <p className="mb-4 text-brown-500">
                      Start from this order — keep or drop items, change
                      quantities, add a note. Your rep will review pricing
                      (supplier costs may have shifted) and come back with a
                      fresh quote.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setReorderError(null);
                        setReorderLines(
                          Object.fromEntries(
                            data.lines.map((l) => [
                              l.id,
                              {
                                included: true,
                                quantity: String(l.quantityTotal),
                              },
                            ]),
                          ),
                        );
                        setReorderNote("");
                        setReorderOpen(true);
                      }}
                      className="rounded-md bg-ink px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700"
                    >
                      Re-order
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="mb-2 font-display text-lg font-bold tracking-tight">
                      Review your re-order
                    </h2>
                    <p className="mb-4 text-brown-500">
                      Keep what you want, set the quantities, and add a note
                      for your rep. Changing a quantity clears that item's
                      existing size and color breakdown — your rep rebuilds
                      it with you while repricing.
                    </p>
                    <div className="mb-4 space-y-2">
                      {data.lines.map((l) => {
                        const st = reorderLines[l.id];
                        if (!st) return null;
                        const qtyNum = Number.parseInt(st.quantity, 10);
                        // Whole number, both bounds. The regex kills the
                        // silent-decimal case ("12.5" parsing to 12).
                        const qtyBad =
                          st.included &&
                          (!/^\d+$/.test(st.quantity.trim()) ||
                            qtyNum < 1 ||
                            qtyNum > 100000);
                        return (
                          <div
                            key={l.id}
                            className="flex flex-wrap items-center gap-3 rounded-md border border-cream-200 bg-white px-4 py-3"
                          >
                            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                              <input
                                type="checkbox"
                                checked={st.included}
                                onChange={(e) =>
                                  setReorderLines((prev) => ({
                                    ...prev,
                                    [l.id]: {
                                      ...prev[l.id]!,
                                      included: e.target.checked,
                                    },
                                  }))
                                }
                                className="h-4 w-4 accent-rust-400"
                              />
                              <span className="min-w-0 flex-1">
                                <span
                                  className={
                                    "block truncate text-sm " +
                                    (st.included
                                      ? "text-brown-500"
                                      : "text-brown-300 line-through")
                                  }
                                >
                                  {lineName(l)}
                                </span>
                                {l.variants.length > 0 && (
                                  <span className="block truncate text-xs text-brown-300">
                                    {l.variants
                                      .map((v) => `${variantLabel(v)} ×${v.quantity}`)
                                      .join(" · ")}
                                  </span>
                                )}
                              </span>
                            </label>
                            <div className="flex items-center gap-2">
                              <label
                                htmlFor={`reorder-qty-${l.id}`}
                                className="text-xs font-semibold uppercase tracking-wider text-brown-300"
                              >
                                Qty
                              </label>
                              <input
                                id={`reorder-qty-${l.id}`}
                                type="number"
                                min={1}
                                max={100000}
                                value={st.quantity}
                                disabled={!st.included}
                                onChange={(e) =>
                                  setReorderLines((prev) => ({
                                    ...prev,
                                    [l.id]: {
                                      ...prev[l.id]!,
                                      quantity: e.target.value,
                                    },
                                  }))
                                }
                                className={
                                  "w-24 rounded-md border bg-white p-2 text-sm text-brown-500 tabular-nums focus:border-peach-400 focus:outline-none disabled:opacity-40 " +
                                  (qtyBad
                                    ? "border-rust-400"
                                    : "border-cream-300")
                                }
                              />
                            </div>
                            {qtyBad && (
                              <p className="w-full text-xs text-rust-500">
                                Enter a whole number between 1 and 100,000.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {!Object.values(reorderLines).some((v) => v.included) && (
                      <p className="mb-4 text-sm text-brown-400">
                        Include at least one item to submit.
                      </p>
                    )}
                    <label
                      htmlFor="reorder-note"
                      className="mb-1 block text-sm font-semibold text-brown-500"
                    >
                      Note for your rep (optional)
                    </label>
                    <textarea
                      id="reorder-note"
                      value={reorderNote}
                      onChange={(e) => setReorderNote(e.target.value)}
                      maxLength={2000}
                      rows={3}
                      placeholder="e.g. Same artwork and locations as last time."
                      className="mb-4 w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                    />
                    {reorderError && (
                      <div className="mb-4 rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
                        {reorderError}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => reorderMutation.mutate()}
                        disabled={
                          reorderMutation.isPending ||
                          !Object.values(reorderLines).some(
                            (v) => v.included,
                          ) ||
                          Object.values(reorderLines).some(
                            (v) =>
                              v.included &&
                              (!/^\d+$/.test(v.quantity.trim()) ||
                                Number.parseInt(v.quantity, 10) < 1 ||
                                Number.parseInt(v.quantity, 10) > 100000),
                          )
                        }
                        className="rounded-md bg-ink px-6 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
                      >
                        {reorderMutation.isPending
                          ? "Submitting…"
                          : "Submit re-order"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReorderOpen(false)}
                        disabled={reorderMutation.isPending}
                        className="rounded-md border border-cream-300 bg-white px-6 py-2.5 font-semibold text-brown-500 shadow-sm transition-colors hover:border-brown-300 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="mt-3 text-sm text-brown-400">
                      Nothing is charged now — your rep confirms pricing with
                      you before anything moves ahead.
                    </p>
                  </>
                )}
              </section>
            )}

            {/* Line items */}
            <section className="mb-8">
              <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
                Line items
              </h2>
              {data.lines.length === 0 ? (
                <div className="rounded-lg border border-cream-200 bg-white px-6 py-12 text-center text-brown-400 shadow-sm">
                  No line items on this order yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {data.lines.map((line) => {
                    // The line's amount comes from the server's shared money
                    // math (goods per colour + decoration charges) — never
                    // re-added here.
                    const each = unitPriceLabel(line);
                    const image = lineImage(line);
                    const locations = line.decorations.filter((d) => !d.isItemCosts);
                    const charges = line.decorations.flatMap((d) => d.charges);
                    return (
                      <div
                        key={line.id}
                        className="rounded-lg border border-cream-200 bg-white p-6 shadow-sm"
                      >
                        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                          {image && (
                            <img
                              src={image}
                              alt={lineName(line)}
                              loading="lazy"
                              className="h-16 w-16 shrink-0 rounded-md border border-cream-200 bg-cream-50 object-contain"
                            />
                          )}
                          <div className="flex-1">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brown-300">
                              Line {line.lineNumber}
                              {lineSku(line) && <> · {lineSku(line)}</>}
                            </div>
                            <h3 className="mt-1 font-display text-lg font-bold tracking-tight text-brown-500">
                              {lineName(line)}
                            </h3>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brown-300">
                              {line.quantityTotal.toLocaleString("en-US")}
                              {each && <> × {each}</>}
                            </div>
                            {line.amount !== null && (
                              <div className="mt-1 font-display text-lg font-bold tracking-tight tabular-nums text-brown-500">
                                {formatUsd(line.amount)}
                              </div>
                            )}
                          </div>
                        </div>

                        {line.variants.length > 0 && (
                          <div className="mb-3">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brown-300">
                              Sizes & colors
                            </div>
                            <ul className="space-y-1 text-sm text-brown-500">
                              {line.variants.map((v) => (
                                <li
                                  key={v.id}
                                  className="flex justify-between border-b border-cream-100 py-1 last:border-b-0"
                                >
                                  <span>{variantLabel(v)}</span>
                                  <span className="font-semibold tabular-nums">
                                    {v.quantity}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {locations.length > 0 && (
                          <div>
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brown-300">
                              Decoration
                            </div>
                            <ul className="space-y-1 text-sm text-brown-500">
                              {locations.map((d) => (
                                <li key={d.id}>{decorationLabel(d)}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Same wording as the client document: what the
                            line's amount includes beyond the goods. */}
                        {charges.length > 0 && (
                          <p className="mt-3 text-sm text-brown-400">
                            Item total includes{" "}
                            {charges
                              .map(
                                (c) =>
                                  `${chargeLabel(c)} ${formatUsd(c.amount)}${c.billingModel === "per_piece" && c.quantity == null ? " each" : ""}${c.quantity != null && c.quantity !== 1 ? ` × ${c.quantity.toLocaleString("en-US")}` : ""}`,
                              )
                              .join(" · ")}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Subtotal / tax / total — the client document's figures. */}
              {data.totals && data.lines.length > 0 && (
                <dl className="mt-4 ml-auto max-w-xs space-y-2 rounded-lg border border-cream-200 bg-white p-6 text-sm shadow-sm">
                  <div className="flex justify-between text-brown-400">
                    <dt>Subtotal</dt>
                    <dd className="tabular-nums">{formatUsd(data.totals.subtotal)}</dd>
                  </div>
                  <div className="flex justify-between text-brown-400">
                    <dt>Tax</dt>
                    <dd className="tabular-nums">{formatUsd(data.totals.tax)}</dd>
                  </div>
                  <div className="flex justify-between border-t border-cream-200 pt-2 font-display text-lg font-bold text-brown-500">
                    <dt>Total</dt>
                    <dd className="tabular-nums">{formatUsd(data.totals.total)}</dd>
                  </div>
                </dl>
              )}
            </section>

            {/* This order's invoices — only ones sent to you or paid. */}
            {data.invoices.length > 0 && <OrderInvoices invoices={data.invoices} />}

            {/* Proofs. Anchor target for the dashboard Action Center's
                "Review proof" links (/orders/:id#proofs). */}
            {data.proofs.length > 0 && (
              <section id="proofs" className="mb-8">
                <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
                  Proofs
                </h2>
                <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {data.proofs.map((p) => (
                    <ProofTile key={p.id} proof={p} orderId={id!} />
                  ))}
                </div>
              </section>
            )}

            {/* Shipments */}
            {data.shipments.length > 0 && (
              <section className="mb-8">
                <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
                  Shipments
                </h2>
                <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">Carrier</th>
                        <th className="px-6 py-3">Tracking</th>
                        <th className="px-6 py-3">Shipped</th>
                        <th className="px-6 py-3">Delivered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.shipments.map((s) => (
                        <tr
                          key={s.id}
                          className="border-b border-cream-100 last:border-b-0"
                        >
                          <td className="px-6 py-3 text-brown-500">
                            {SHIPMENT_STATUS_LABEL[s.status]}
                          </td>
                          <td className="px-6 py-3 text-brown-400">
                            {s.carrier ?? "—"}
                          </td>
                          <td className="px-6 py-3">
                            {s.trackingUrl && s.trackingNumber ? (
                              <a
                                href={s.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-ink underline-offset-2 hover:underline"
                              >
                                {s.trackingNumber}
                              </a>
                            ) : s.trackingNumber ? (
                              <span className="text-brown-500">
                                {s.trackingNumber}
                              </span>
                            ) : (
                              <span className="text-brown-300">—</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-brown-400">
                            {formatDate(s.actualShipDate ?? s.projectedShipDate)}
                          </td>
                          <td className="px-6 py-3 text-brown-400">
                            {formatDate(s.deliveredAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
    </PortalLayout>
  );
}

/**
 * The order's progress. Horizontal on desktop, vertical on a phone. Each
 * step's status and date come from the server (portalOrderTimeline); a date
 * is shown only when one exists.
 */
function OrderTimeline({ timeline }: { timeline: PortalOrderTimeline }) {
  if (timeline.cancelled) {
    return (
      <section className="mb-8 rounded-lg border border-cream-200 bg-white px-6 py-4 text-sm text-brown-400 shadow-sm">
        This order was cancelled.
      </section>
    );
  }
  return (
    <section
      aria-label="Order progress"
      className="mb-8 rounded-lg border border-cream-200 bg-white p-6 shadow-sm"
    >
      <ol className="flex flex-col gap-4 sm:flex-row sm:gap-0">
        {timeline.steps.map((step, i) => {
          const last = i === timeline.steps.length - 1;
          const nextDone = timeline.steps[i + 1]?.status === "done";
          return (
            <li
              key={step.key}
              aria-current={step.status === "current" ? "step" : undefined}
              className="relative flex items-start gap-3 sm:flex-1 sm:flex-col sm:items-center sm:gap-2 sm:text-center"
            >
              {/* Connector to the next step: down on a phone, across on desktop. */}
              {!last && (
                <span
                  aria-hidden
                  className={
                    "absolute left-[11px] top-7 -bottom-4 w-0.5 sm:left-[calc(50%+0.875rem)] sm:right-[calc(-50%+0.875rem)] sm:top-3 sm:bottom-auto sm:h-0.5 sm:w-auto " +
                    (nextDone ? "bg-ink" : "bg-cream-300")
                  }
                />
              )}
              <span
                aria-hidden
                className={
                  "relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold " +
                  (step.status === "done"
                    ? "bg-ink text-white"
                    : step.status === "current"
                      ? "border-2 border-ink bg-white text-brown-500"
                      : "border border-cream-400 bg-white text-brown-300")
                }
              >
                {step.status === "done" ? "✓" : i + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={
                    "block text-sm " +
                    (step.status === "upcoming"
                      ? "text-brown-300"
                      : "font-semibold text-brown-500")
                  }
                >
                  {step.label}
                  <span className="sr-only">
                    {step.status === "done" ? " (done)" : step.status === "current" ? " (next)" : ""}
                  </span>
                </span>
                {step.date && (
                  <span className="block text-xs text-brown-400">{formatDate(step.date)}</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** This order's invoices, each linking to its own page in the portal. */
function OrderInvoices({ invoices }: { invoices: PortalOrderInvoice[] }) {
  return (
    <section className="mb-8">
      <h2 className="mb-4 font-display text-xl font-bold tracking-tight">Invoices</h2>
      <div className="relative overflow-x-auto rounded-lg border border-cream-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
              <th className="px-6 py-3">Invoice #</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3 text-right">Total</th>
              <th className="px-6 py-3 text-right">Balance</th>
              <th className="px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-cream-100 last:border-b-0">
                <td className="px-6 py-3">
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="font-semibold text-ink underline-offset-2 hover:underline"
                  >
                    #{inv.invoiceNumber}
                  </Link>
                </td>
                <td className="px-6 py-3 text-brown-500">{INVOICE_TYPE_LABEL[inv.invoiceType]}</td>
                <td className="px-6 py-3 text-brown-400">{formatDate(inv.invoiceDate)}</td>
                <td className="px-6 py-3 text-right tabular-nums text-brown-500">
                  {formatUsd(inv.total)}
                </td>
                <td className="px-6 py-3 text-right font-semibold tabular-nums text-brown-500">
                  {formatUsd(inv.balanceDue)}
                </td>
                <td className="px-6 py-3">
                  <span
                    className={
                      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold " +
                      INVOICE_STATUS_PILL_CLASS[inv.status]
                    }
                  >
                    {INVOICE_STATUS_LABEL[inv.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Phase 9.3.B — Proof preview tile. Fetches a presigned download URL
 * via `/portal/files/:id/download` for image proofs; falls back to the
 * filename for non-image proofs (PDF previews are deferred — clients
 * can open the file via the explicit download link).
 *
 * 8-min cache mirrors the 10-min URL TTL on the server side. Re-renders
 * stay snappy even across long browsing sessions.
 */
function ProofTile({
  proof,
  orderId,
}: {
  proof: PortalProof;
  orderId: string;
}) {
  const isImage = proof.mimeType.startsWith("image/");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  // Download (CEO 2026-10-19): a fresh presigned URL on click, through the
  // same tenancy-checked /portal/files/:id/download the preview uses — the
  // pattern BrandAssetsPage's DownloadLazyButton follows.
  async function onDownload(): Promise<void> {
    setDownloading(true);
    setDownloadError(false);
    try {
      const res = await fetchPortalFileDownloadUrl(proof.fileId);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  }
  const { data: download } = useQuery({
    queryKey: ["portal-file-download", proof.fileId],
    queryFn: () => fetchPortalFileDownloadUrl(proof.fileId),
    enabled: isImage,
    staleTime: 8 * 60 * 1000,
  });

  return (
    <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
      <div className="flex h-48 items-center justify-center bg-cream-100">
        {isImage && download?.url ? (
          <img
            src={download.url}
            alt={proof.originalFilename}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="text-sm text-brown-300">
            {isImage ? "Loading preview…" : "Preview unavailable"}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
          {PROOF_STATUS_LABEL[proof.status]} · v{proof.version}
        </div>
        <div
          className="truncate text-sm text-brown-500"
          title={proof.originalFilename}
        >
          {proof.originalFilename}
        </div>
        <button
          type="button"
          onClick={() => void onDownload()}
          disabled={downloading}
          className="mt-2 text-sm font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50"
        >
          {downloading ? "Opening…" : "Download"}
        </button>
        {downloadError && (
          <p className="mt-1 text-sm text-rust-500">
            We couldn't open this file. Try again in a moment.
          </p>
        )}
        {proof.status === "awaiting_client" && (
          <ProofDecisionActions proofId={proof.id} orderId={orderId} />
        )}
      </div>
    </div>
  );
}

/**
 * Portal v2 Slice A — the client's proof decision, in the portal.
 *
 * Approve opens the sign-off (Stephanie 2026-10-14, wording Jake
 * 2026-10-19): tick the terms and type your full name — the same form the
 * emailed proof link shows, from GET /proof-terms. It starts production;
 * both doors run the identical server-side decision core. "Request changes" requires a reason so
 * the rep knows what to fix. 409 = someone else (or an emailed link)
 * already decided; we refresh the order to show the new state.
 */
function ProofDecisionActions({
  proofId,
  orderId,
}: {
  proofId: string;
  orderId: string;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"idle" | "confirm-approve" | "reject">(
    "idle",
  );
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const termsQ = useQuery({
    queryKey: ["proof-terms"],
    queryFn: fetchProofTerms,
    enabled: mode === "confirm-approve",
    staleTime: 60 * 60 * 1000,
  });
  const terms = termsQ.data;
  const canSign = !!terms && agreed && signedName.trim().length >= 2;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["portal-order", orderId] });
    void queryClient.invalidateQueries({ queryKey: ["portal-attention"] });
  };

  const onError = (err: unknown) => {
    const body = err instanceof ApiError ? (err.body as { code?: string; error?: string } | null) : null;
    if (body?.code === "signature_required" && body.error) {
      setActionError(body.error);
      if (err instanceof ApiError && err.status === 409) void termsQ.refetch();
    } else if (err instanceof ApiError && err.status === 409) {
      setActionError(
        "This proof was already decided — refreshing to the latest state.",
      );
      refresh();
    } else {
      setActionError("Something went wrong. Try again in a moment.");
    }
  };

  const approveMutation = useMutation({
    mutationFn: () => approvePortalProof(proofId, { signedName: signedName.trim(), termsVersion: terms?.version ?? "" }),
    onSuccess: () => {
      setActionError(null);
      setMode("idle");
      refresh();
    },
    onError,
  });
  const rejectMutation = useMutation({
    mutationFn: () => rejectPortalProof(proofId, reason.trim()),
    onSuccess: () => {
      setActionError(null);
      setMode("idle");
      refresh();
    },
    onError,
  });
  const busy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="mt-3 border-t border-cream-100 pt-3">
      {mode === "idle" && (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode("confirm-approve")}
            className="rounded-md bg-ink px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
          >
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode("reject")}
            className="rounded-md border border-cream-300 bg-cream-50 px-3 py-1.5 text-sm font-semibold text-brown-500 transition-colors hover:bg-cream-100 disabled:opacity-50"
          >
            Request changes
          </button>
        </div>
      )}
      {mode === "confirm-approve" && (
        <div>
          {!terms ? (
            <p className="mb-2 text-sm text-brown-400">
              {termsQ.isError ? "We couldn't load the approval terms. Try again in a moment." : "Loading…"}
            </p>
          ) : (
            <div className="mb-3 space-y-3 text-sm text-brown-500">
              <p className="font-semibold">{terms.note}</p>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
                />
                <span>
                  <span className="font-semibold">{terms.agree}</span>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-brown-400">
                    {terms.points.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </span>
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="block min-w-[220px] flex-1">
                  <span className="mb-1 block font-semibold">Full name (your signature)</span>
                  <input
                    type="text"
                    value={signedName}
                    onChange={(e) => setSignedName(e.target.value)}
                    maxLength={120}
                    autoComplete="name"
                    className="h-9 w-full rounded-md border border-cream-300 bg-white px-2 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                  />
                </label>
                <div>
                  <span className="mb-1 block font-semibold">Date</span>
                  <p className="flex h-9 items-center rounded-md border border-cream-300 bg-cream-50 px-2 text-brown-400">
                    {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !canSign}
              onClick={() => approveMutation.mutate()}
              className="rounded-md bg-ink px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {approveMutation.isPending ? "Approving…" : "Approve proof"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode("idle")}
              className="rounded-md border border-cream-300 bg-cream-50 px-3 py-1.5 text-sm font-semibold text-brown-500 transition-colors hover:bg-cream-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {mode === "reject" && (
        <div>
          <label
            htmlFor={`reject-reason-${proofId}`}
            className="mb-1 block text-sm font-semibold text-brown-500"
          >
            What should we change?
          </label>
          <textarea
            id={`reject-reason-${proofId}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            rows={3}
            className="mb-2 w-full rounded-md border border-cream-300 bg-white p-2 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
            placeholder="e.g. Logo should be centered, thread color looks too dark…"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || reason.trim().length === 0}
              onClick={() => rejectMutation.mutate()}
              className="rounded-md bg-ink px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {rejectMutation.isPending ? "Sending…" : "Send feedback"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode("idle")}
              className="rounded-md border border-cream-300 bg-cream-50 px-3 py-1.5 text-sm font-semibold text-brown-500 transition-colors hover:bg-cream-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {actionError && (
        <p className="mt-2 text-sm text-rust-500">{actionError}</p>
      )}
    </div>
  );
}
